import { useState, useEffect } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import InfiniteScroll from 'react-infinite-scroller';

import Button from 'src/components/NewButton';
import Checkbox from 'src/components/Checkbox';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      backgroundColor: '#fff',
      borderRadius: 8,
      boxShadow:
        '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
      padding: 24,
      width: 600,
      maxHeight: 600,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      marginBottom: 16,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      marginBottom: 12,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: 14,
    },
    options: {
      flex: 1,
      overflowY: 'auto',
      maxHeight: 400,
    },
    option: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 0',
    },
    actions: {
      display: 'flex',
      gap: 8,
      marginTop: 16,
      justifyContent: 'flex-end',
    },
  })
);

const RISK_COLUMNS = [
  'episode_scores_1. Adult Sexual Explicit Risk',
  'episode_scores_2. Arms & Ammunition  Risk',
  'episode_scores_3. Crime & Violation of Human Rights  Risk',
  'episode_scores_4. Death, Injury or Military Conflict Risk',
  'episode_scores_5 & 9. Online Piracy & Spam Risk',
  'episode_scores_6. Hate Speech & Acts of Aggression Risk',
  'episode_scores_7. Obscenity & Profanity Risk',
  'episode_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  'episode_scores_10. Terrorism Risk',
  'episode_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  'episode_scores_GAMBLING Risk',
  'episode_scores_OCCULT Risk',
  'episode_scores_NATURAL DISASTER Risk',
  'show_scores_1. Adult Sexual Explicit Risk',
  'show_scores_2. Arms & Ammunition  Risk',
  'show_scores_3. Crime & Violation of Human Rights  Risk',
  'show_scores_4. Death, Injury or Military Conflict Risk',
  'show_scores_5 & 9. Online Piracy & Spam Risk',
  'show_scores_6. Hate Speech & Acts of Aggression Risk',
  'show_scores_7. Obscenity & Profanity Risk',
  'show_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  'show_scores_10. Terrorism Risk',
  'show_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  'show_scores_GAMBLING Risk',
  'show_scores_OCCULT Risk',
  'show_scores_NATURAL DISASTER Risk',
];

interface RiskColumnSelectorProps {
  open: boolean;
  onClose: () => void;
  selectedColumns: string[];
  onSelect: (columns: string[]) => void;
}

export const RiskColumnSelector = ({
  open,
  onClose,
  selectedColumns,
  onSelect,
}: RiskColumnSelectorProps) => {
  const classes = useStyles();
  const [searchKey, setSearchKey] = useState('');
  const [selectedOptions, setSelectedOptions] =
    useState<string[]>(selectedColumns);
  const [records, setRecords] = useState(100);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (open) {
      setSearchKey('');
      setSelectedOptions(selectedColumns);
      setRecords(100);
      setHasMore(true);
    }
  }, [open, selectedColumns]);

  const filteredColumns = RISK_COLUMNS.filter(
    (item: string) =>
      !searchKey ||
      (item && item.toLowerCase().includes(searchKey.toLowerCase()))
  );

  const loadMore = () => {
    if (records >= filteredColumns.length) {
      setHasMore(false);
    } else {
      setTimeout(() => {
        setRecords(records + 100);
      }, 500);
    }
  };

  const handleSave = () => {
    onSelect(selectedOptions);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box className={classes.paper}>
        <Box className={classes.header}>
          <Typography variant='h6' style={{ fontSize: 18, fontWeight: 600 }}>
            Select Risk Columns
          </Typography>
        </Box>
        <Box className={classes.searchContainer}>
          <SearchIcon style={{ color: '#667085' }} />
          <input
            type='text'
            placeholder='Search risk columns...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className={classes.searchInput}
          />
        </Box>
        <Box className={classes.options}>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={hasMore}
            loader={<div key={0}>Loading...</div>}
            useWindow={false}
          >
            {filteredColumns.slice(0, records).map((column) => (
              <Box key={column} className={classes.option}>
                <Checkbox
                  name={column}
                  label=''
                  checked={selectedOptions.includes(column)}
                  onChange={() => {
                    if (selectedOptions.includes(column)) {
                      setSelectedOptions(
                        selectedOptions.filter((c) => c !== column)
                      );
                    } else {
                      setSelectedOptions([...selectedOptions, column]);
                    }
                  }}
                />
                <Typography style={{ fontSize: 14 }}>{column}</Typography>
              </Box>
            ))}
          </InfiniteScroll>
        </Box>
        <Box className={classes.actions}>
          <Button
            title='Cancel'
            variant='outlined'
            height={36}
            onClick={onClose}
          />
          <Button title='Select' height={36} onClick={handleSave} />
        </Box>
      </Box>
    </Modal>
  );
};
